/**
 * Pure numeric helpers for the workbench inspector's paired numeric rows.
 *
 * Kept free of React so the stepper clamping, the keyboard increment rule and
 * the ratio-lock maths can be unit-tested directly. Every one of these is a
 * place a silent no-op likes to hide: a clamp that reads `Math.min` where it
 * meant `Math.max`, a ratio lock that drifts a few pixels per keystroke until
 * the layer is visibly wrong.
 */

/** Clamp `value` into `[min, max]`. Missing bounds are treated as unbounded. */
export function clampValue(value: number, min?: number, max?: number): number {
  let next = value;
  if (typeof min === 'number' && next < min) next = min;
  if (typeof max === 'number' && next > max) next = max;
  return next;
}

/**
 * Snap to the field's step grid and kill floating-point dust, so 1.2 + 0.1
 * renders as `1.3` and never as `1.3000000000000003`.
 */
export function roundToStep(value: number, step: number): number {
  if (!Number.isFinite(step) || step <= 0) return value;
  const decimals = decimalsForStep(step);
  return Number((Math.round(value / step) * step).toFixed(decimals));
}

/** How many decimal places a step implies — 1 → 0, 0.1 → 1, 0.01 → 2. */
export function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  const text = String(step);
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

/**
 * One keyboard/stepper increment.
 *
 * `shift` multiplies the step by 10 — the Figma/Sketch convention every design
 * tool shares, so a user nudging a layer by 10px does not hold the arrow key.
 */
export function applyStep(
  value: number,
  options: { step: number; direction: 1 | -1; shift?: boolean; min?: number; max?: number },
): number {
  const { step, direction, shift = false, min, max } = options;
  const magnitude = step * (shift ? 10 : 1);
  return clampValue(roundToStep(value + magnitude * direction, step), min, max);
}

/** Parse user-typed text, falling back to the last good value rather than NaN. */
export function parseNumeric(input: string, fallback: number): number {
  const cleaned = input.replace(/[^0-9.-]/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export interface RatioBounds {
  aMin?: number;
  aMax?: number;
  bMin?: number;
  bMax?: number;
}

export interface RatioPair {
  a: number;
  b: number;
}

/**
 * Ratio-locked edit of a W/H pair.
 *
 * `ratio` is `a / b`, captured when the lock was engaged — NOT recomputed from
 * the current pair on every keystroke. Recomputing is the bug: each rounded
 * write shifts the ratio slightly, and twenty keystrokes later the proportion
 * the user locked is gone.
 *
 * Clamping is applied on both axes and then reconciled: if the partner axis
 * hits a bound, the edited axis is pulled back so the pair still satisfies the
 * ratio. A lock that silently stops being a lock at the bounds is worse than no
 * lock at all.
 */
export function applyRatioLink(params: {
  axis: 'a' | 'b';
  nextValue: number;
  ratio: number;
  bounds?: RatioBounds;
  step?: number;
}): RatioPair {
  const { axis, nextValue, ratio, bounds = {}, step = 1 } = params;
  const { aMin, aMax, bMin, bMax } = bounds;

  // A nonsense ratio must not silently corrupt the pair — hold the edited axis
  // and leave the partner where the caller last had it.
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return { a: clampValue(nextValue, aMin, aMax), b: clampValue(nextValue, bMin, bMax) };
  }

  if (axis === 'a') {
    const firstA = clampValue(roundToStep(nextValue, step), aMin, aMax);
    const b = clampValue(roundToStep(firstA / ratio, step), bMin, bMax);
    return { a: clampValue(roundToStep(b * ratio, step), aMin, aMax), b };
  }

  const firstB = clampValue(roundToStep(nextValue, step), bMin, bMax);
  const a = clampValue(roundToStep(firstB * ratio, step), aMin, aMax);
  return { a, b: clampValue(roundToStep(a / ratio, step), bMin, bMax) };
}

/** The ratio to freeze when the link toggle is switched on. */
export function captureRatio(pair: RatioPair): number {
  if (!Number.isFinite(pair.a) || !Number.isFinite(pair.b) || pair.b === 0) return 1;
  return pair.a / pair.b;
}
